// @flow

import * as React from 'react';

import { ProductKindBadge } from '@gandi/cart';
import type { ProductId, Process, ProductType, ProductKind } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Text } from '@gandi/ui';

import { ListRow as SuperNewListRow } from '../../../ListRow';

import ProductSetup from '../ProductSetup';
import {
  useDomainPerksDetails,
  useStudioWebhostingPerksDetails,
  useWebhostingPerksDetails,
} from '../modules/hooks';

import Autorenew from './Autorenew';

import styles from './ProductKindHeader.scss';

const AUTORENEW_ENABLED = [
  'DomainCreate',
  'DomainTransfer',
  'PaasCreate',
  'promotional',
  'MailboxStandAloneCreate',
  'Mailboxv2Create',
];

type Props = {
  authenticated: boolean,
  cartProductOwner: ?string,
  firstProductId: ProductId,
  hasWebhosting: boolean,
  kind: ProductKind,
  productType: ProductType,
  process: Process,
};

export function ProductKindHeader({
  authenticated,
  cartProductOwner,
  firstProductId,
  hasWebhosting,
  kind,
  process,
  productType,
}: Props): React.MixedElement {
  const showSetup = authenticated && kind !== 'services';

  const showAutorenew =
    authenticated &&
    !cartProductOwner &&
    !hasWebhosting &&
    AUTORENEW_ENABLED.includes(kind);

  const isDomainCreate = kind === 'DomainCreate';
  const isPaasCreate = kind === 'PaasCreate';
  const isStudioProduct = kind === 'StudioConvert';

  return (
    <>
      <ProductKindBadge kind={kind} productType={productType} />

      <div className={styles.root}>
        {showSetup && (
          <ProductSetup
            process={process}
            productType={productType}
            firstProductId={firstProductId}
          />
        )}

        <div className={styles.blocks}>
          {showAutorenew && (
            <Autorenew
              kind={kind}
              productType={productType}
              process={process}
            />
          )}

          {isDomainCreate && <IncludedWithDomain />}
          {isPaasCreate && <IncludedWithPaas />}
          {isStudioProduct && <IncludedWithStudio />}
        </div>
      </div>

      <hr />
    </>
  );
}

ProductKindHeader.Error = function ProductKindHeaderError({
  kind,
}: {
  kind: ProductKind,
}): React.MixedElement {
  const productType: any = kind
    .replace(/([A-Z].+?)[A-Z].+/, '$1')
    .toLowerCase();
  return <ProductKindBadge kind={kind} productType={productType} />;
};

function IncludedWithDomain(): React.MixedElement {
  const { __ } = useTranslator();
  const perks = useDomainPerksDetails();

  return (
    <SuperNewListRow
      intent="muted"
      title={__('Included with every domain purchase')}
      options={perks.map(({ label, detail }) => (
        <Perk key={label} label={label} detail={detail} />
      ))}
      price={<Text color="weak">{__('Free')}</Text>}
    />
  );
}

function IncludedWithPaas(): React.MixedElement {
  const { __ } = useTranslator();
  const perks = useWebhostingPerksDetails();

  return (
    <SuperNewListRow
      intent="muted"
      title={__('Included with every Web Hosting purchase')}
      options={perks.map(({ label, detail }) => (
        <Perk key={label} label={label} detail={detail} />
      ))}
      price={<Text color="weak">{__('Free')}</Text>}
    />
  );
}

function IncludedWithStudio(): React.MixedElement {
  const { __ } = useTranslator();
  const perks = useStudioWebhostingPerksDetails();

  return (
    <SuperNewListRow
      intent="muted"
      title={__('Included with every domain name')}
      options={perks.map(({ label, detail }) => (
        <Perk key={label} label={label} detail={detail} />
      ))}
    />
  );
}

function Perk({
  label,
  detail,
}: {
  label: string,
  detail?: string,
}): React.MixedElement {
  return (
    <>
      {label} {detail && <Text color="weak">– {detail}</Text>}
    </>
  );
}
